import { isPlatformBrowser } from '@angular/common'
import {
  Component,
  HostListener,
  Inject,
  OnInit,
  PLATFORM_ID,
  Renderer2,
  ViewEncapsulation
} from '@angular/core'
import { DomSanitizer, SafeHtml } from '@angular/platform-browser'
import { ActivatedRoute, Params, Router } from '@angular/router'

import { AuthorLayout } from '../../../../../shared/enums/author-layout.enum'
import { DocumentLayout } from '../../../../../shared/enums/document-layout.enum'
import { Author } from '../../../../../shared/interfaces/author.interface'
import { BreadcrumbLink } from '../../../../../shared/interfaces/breadcrumb-link.interface'
import { ImageSet } from '../../../../../shared/interfaces/image-set.interface'
import { Post } from '../../../../../shared/interfaces/resources/post.interface'
import { WorkingGroup } from '../../../../../shared/interfaces/resources/working-group.interface'
import { StripHtmlPipe } from '../../../../../shared/pipes/strip-html.pipe'
import { AuthorService } from '../../../../../shared/services/author.service'
import { BreadcrumbService } from '../../../../../shared/services/breadcrumb.service'
import { DomContentService } from '../../../../../shared/services/dom-content.service'
import { MediaService } from '../../../../../shared/services/media.service'
import { MetaService } from '../../../../../shared/services/meta.service'
import { PostService } from '../../../../../shared/services/post.service'
import { WorkingGroupService } from '../../../../../shared/services/working-group.service'

@Component({
  selector: 'app-post-detail',
  templateUrl: './post-detail.component.html',
  styleUrls: ['./post-detail.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class PostDetailComponent implements OnInit {
  post: Post
  postContent: SafeHtml
  relatedPosts: Post[]
  workingGroup: WorkingGroup
  coAuthors: Author[]
  authorImage: ImageSet

  minRead: number
  private wordsPerMinute = 250

  breadcrumbLinks: BreadcrumbLink[]

  DocumentLayout = DocumentLayout
  AuthorLayout = AuthorLayout

  loading: boolean
  loadingRelatedPosts: boolean
  showCustomTooltip: boolean

  constructor(
    private postService: PostService,
    private workingGroupService: WorkingGroupService,
    private authorService: AuthorService,
    private activatedRoute: ActivatedRoute,
    private metaService: MetaService,
    private stripHtmlPipe: StripHtmlPipe,
    private breadcrumbService: BreadcrumbService,
    private sanitizer: DomSanitizer,
    private renderer: Renderer2,
    private router: Router,
    private mediaService: MediaService,
    private domContentService: DomContentService,
    @Inject(PLATFORM_ID) private platformId: object
  ) {}

  ngOnInit() {
    this.activatedRoute.params.subscribe((params: Params) => {
      this.loading = true

      this.postService.show({ slug: params.slug }).then(
        async (res: Post) => {
          this.loading = false
          this.post = res
          this.postContent = this.sanitizer.bypassSecurityTrustHtml(
            this.post.content
          )

          this.authorService
            .show(this.post.author.slug)
            .then((authorRes: Author) => {
              this.authorImage = authorRes.image
            })

          this.minRead = this.post.content
            ? Math.ceil(
                this.stripHtmlPipe.transform(this.post.content).split(' ')
                  .length / this.wordsPerMinute
              )
            : 0

          // Meta and breadcrumbs.
          this.metaService.setTags({
            title: this.post.metaTitle || this.post.title,
            description: this.stripHtmlPipe.transform(
              this.post.metaDescription || this.post.excerpt
            ),
            path: `wonk/${this.post.slug}`,
            absoluteOgImage: this.post.image
              ? this.post.image.socialCard
              : this.metaService.defaultMeta.absoluteOgImage
          })

          this.breadcrumbLinks = this.breadcrumbService.getBreadCrumbLinks(
            'blogIndex',
            [
              {
                label: this.post.title,
                path: `wonk/${this.post.slug}`
              }
            ]
          )
          // Get full WorkingGroup if related (to get thumbnail).
          if (this.post.workingGroup) {
            this.workingGroup = await this.workingGroupService.show(
              this.post.workingGroup.id
            )
          }

          if (this.post.coAuthorIds.length) {
            this.authorService
              .listByIds(this.post.coAuthorIds)
              .then((coAuthorRes: Author[]) => {
                this.coAuthors = coAuthorRes
              })
          }

          // Get related posts (same policy area).
          this.loadingRelatedPosts = true
          this.postService
            .listUnpaginated({
              policyAreaId: this.post.policyAreas.length
                ? this.post.policyAreas[0].id.toString()
                : null,
              limitTo: 3
            })
            .then((postRes: Post[]) => {
              this.loadingRelatedPosts = false
              this.relatedPosts = postRes.filter((p) => p.id !== this.post.id)
            })

          // Custom tooltips.
          if (isPlatformBrowser(this.platformId)) {
            setTimeout(() => {
              this.domContentService.enableTooltips(this.renderer)
              this.domContentService.enableAccordions()
            }, 0)
          }
        },
        () => {
          this.router.navigate(['/404'])
        }
      )
    })
  }

  @HostListener('document:click', ['$event.target'])
  onClick(target: any) {
    // We track clicks to close model if open and click outside
    if (this.showCustomTooltip && target.id !== 'custom-tooltip-html-wrapper') {
      this.showCustomTooltip = false
    }
  }
}
